/**
 * The entry point of the Angular application, where the root component is bootstrapped and
 * various providers are configured.
 *
 * This file sets up the application by importing necessary modules, configuring HTTP interceptors,
 * setting up internationalization (i18n) with ngx-translate, and other essential services.
 *
 * Dependencies:
 * - Angular core modules for application bootstrapping and animation.
 * - Angular Material modules for UI components.
 * - ngx-translate for internationalization support.
 * - Toastr for notification messages.
 * - Ngx Markdown for rendering markdown content.
 * - Angular's HTTP client for making HTTP requests with interceptors.
 *
 * Modules and Services:
 * - `MatSnackBarModule`: Provides a service for displaying brief messages at the bottom of the screen.
 * - `provideHttpClient`: Configures the HTTP client for the application.
 * - `HTTP_INTERCEPTORS`: Allows the configuration of multiple HTTP interceptors for handling requests/responses.
 * - `TranslateHttpLoader`: Loads translation files from a specified path.
 * - `MarkdownModule`: Enables rendering of markdown content within the application.
 *
 * @module MainApplication
 * @returns {void} This function does not return a value but bootstraps the Angular application.
 */

/**
 * Factory function for creating an instance of the TranslateHttpLoader.
 *
 * @function HttpLoaderFactory
 * @param {HttpClient} http - The HttpClient instance used to fetch translation files.
 * @returns {TranslateHttpLoader} An instance of TranslateHttpLoader configured with the provided HttpClient.
 *
 * Functionality:
 * - Creates a loader that fetches translation JSON files from the specified directory ('./assets/i18n/')
 *   with the specified file extension ('.json').
 */

/**
 * Bootstraps the Angular application with the root component and provider configuration.
 *
 * @function bootstrapApplication
 * @param {AppComponent} AppComponent - The root component of the Angular application.
 * @param {Object} options - Configuration options for bootstrapping the application.
 * @param {Array} options.providers - An array of provider configurations used for dependency injection.
 *
 * Configuration Overview:
 * - Provides routing capabilities with `provideRouter` using the defined `ROUTES`.
 * - Provide APP_INITIALIZER to load environment from external json
 * - Configures various UI and service modules, including:
 *   - `MatSnackBarModule`: For displaying snack bar messages.
 *   - `provideAnimations`: To enable Angular animations.
 *   - `provideToastr`: For configuring Toastr notifications.
 *   - `provideHttpClient`: For making HTTP requests with configured interceptors.
 * - Registers custom HTTP interceptors:
 *   - `ApiInterceptor`: Handles API request modifications.
 *   - `ErrorInterceptor`: Handles errors during HTTP requests.
 *   - `AuthInterceptor`: Manages authentication tokens for API calls.
 * - Sets up internationalization with ngx-translate using `TranslateModule`.
 * - Integrates the Markdown module for rendering markdown content in the application.
 * - Ensures that the HttpClient module is available for making HTTP requests.
 *
 * @throws {Error} Catches and logs any errors that occur during the bootstrap process.
 */

import {
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { AppComponent } from './app/app.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication } from '@angular/platform-browser';
import { ApiInterceptor } from './app/services/interceptors/api.interceptor';
import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptors,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { ROUTES } from './app/app-routes';
import { ErrorInterceptor } from './app/services/interceptors/error.interceptor';
import { AuthInterceptor } from './app/services/interceptors/auth.interceptor';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { provideToastr } from 'ngx-toastr';
import { MarkdownModule } from 'ngx-markdown';

import { environment } from './environments/environment';
import { EnvironmentService } from './app/services/environment/environment.service';
import { lastValueFrom } from 'rxjs';

/**
 * Call environments loading service and set environments values
 */
export function loadEnviroments(environmentService: EnvironmentService) {
    /**
     * Call environmentService and await the response from loadEnvironments()
     * and set environment object properties with loaded data
     */
    return async () => {
        const config = await lastValueFrom(
            environmentService.loadEnvironments(),
        );
        /**
         *  Set environment value before app bootstrap
         */
        environment.apiUrl = config.apiUrl;
        environment.documentationUrl = config.documentationUrl;
        environment.production = config.production;
        /*
         * If minPasswordLength not exist on environment.json use the default value set in environment.ts (default is 8)
         * If authenticationType not exist on environment.json use the default value set in environment.ts (default is "" for Mua users)
         */
        environment.minPasswordLength =
            (config as any)['minPasswordLength'] ??
            environment.minPasswordLength;
        environment.authenticationType =
            (config as any)['authenticationType'] ??
            environment.authenticationType;
    };
}

/**
 * Factory function to create a TranslateHttpLoader instance.
 *
 * This function is used to configure the `TranslateModule` for internationalization.
 * It creates a `TranslateHttpLoader` instance, specifying the base URL for language files and the file extension.

 * @param http {HttpClient} The HTTP client to use for loading language files.
 * @returns {TranslateHttpLoader} The created `TranslateHttpLoader` instance.
 */
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, './i18n/', '.json')
}

/**
 * Bootstraps the Angular application and provides necessary providers.

 * This function configures the application with:
 * - Routing: Using `provideRouter` to set up application routes.
 * - Material Design: Importing `MatSnackBarModule` for snackbar notifications.
 * - Animations: Using `provideAnimations` for browser animations.
 * - Toasts: Using `provideToastr` for toast notifications.
 * - HTTP Interceptors:
 - `ApiInterceptor`: For API-specific interception (e.g., adding headers, error handling).
 - `ErrorInterceptor`: For global error handling (e.g., token refresh).
 - `AuthInterceptor`: For authentication and authorization.
 * - Internationalization: Using `TranslateModule` with `HttpLoaderFactory` to load language files.
 * - Markdown: Using `MarkdownModule` for rendering Markdown content.
 * - HTTP Client: Using `HttpClientModule` for making HTTP requests.

 * @returns {Promise<void>} A Promise that resolves when the application is bootstrapped successfully.
 */
bootstrapApplication(AppComponent, {
    providers: [
        /**
         *  Configure APP_INITIALIZER to load from EnvironmentService the environments data before app start
         */
        provideZoneChangeDetection(),
        provideAppInitializer(() => {
            const initializerFn = loadEnviroments(inject(EnvironmentService));
            return initializerFn();
        }),
        provideRouter(ROUTES),
        importProvidersFrom([MatSnackBarModule]),
        provideAnimations(),
        provideToastr(),

        provideHttpClient(withInterceptors([ApiInterceptor]), withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },

        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient],
                },
            }),
        ),
        importProvidersFrom(MarkdownModule.forRoot()),
    ],
}).catch((err) => console.error(err));
